import PocketBase from 'pocketbase';
import { POCKETBASE_URL } from './config';

// Создаём singleton клиент
export const pb = new PocketBase(POCKETBASE_URL);

// Автосохранение auth в localStorage
pb.autoCancellation(false);

// Типы данных

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  telegram_id?: string;
  telegram_username?: string;
  subscription_tier: 'free' | 'creator' | 'top_creator';
  subscription_end_date?: string;
  subscription_billing_period?: 'monthly' | 'yearly';
  marketing_consent: boolean;
  promo_code_used?: string;
  verified: boolean;
  created: string;
  updated: string;
}

export interface Project {
  id: string;
  user: string;
  name: string;
  market: string;
  stage: 'Только идея' | 'Есть MVP' | 'Работающий продукт';
  revenue?: string;
  description?: string;
  is_active: boolean;
  created: string;
  updated: string;
}

export interface MarketAnalysis {
  id: string;
  user: string;
  project: string;
  product_description: string;
  keywords?: string;
  competitors?: string;
  advantage?: string;
  comment?: string;
  generated_text?: string;
  status: 'pending' | 'completed' | 'failed';
  created: string;
}

export interface Persona {
  id: string;
  user: string;
  project: string;
  persona_name: string;
  persona_data?: Record<string, unknown>;
  generated_text?: string;
  avatar_prompt?: string;
  created: string;
}

export interface Positioning {
  id: string;
  user: string;
  project: string;
  market_analysis?: string;
  persona?: string;
  generated_text?: string;
  created: string;
}

export interface MvpPlan {
  id: string;
  user: string;
  project: string;
  positioning?: string;
  generated_text?: string;
  input_data?: {
    marketAnalysisId?: string;
    personaId?: string;
    positioningId?: string;
    businessModelId?: string;
    budget?: string;
    comment?: string;
  };
  created: string;
}

export interface BusinessModel {
  id: string;
  user: string;
  project: string;
  positioning?: string;
  market_analysis?: string;
  generated_text?: string;
  input_data?: {
    positioningId?: string;
    marketAnalysisId?: string;
    revenueHint?: string;
    targetMarketSize?: string;
    comment?: string;
  };
  created: string;
}

export interface MarketingStrategy {
  id: string;
  user: string;
  project: string;
  market_analysis?: string;
  persona?: string;
  positioning?: string;
  generated_text?: string;
  status: 'pending' | 'completed' | 'failed';
  input_data?: {
    budget?: string;
    mainGoal?: string;
    teamSize?: string;
    currentChannels?: string;
    marketAnalysisId?: string;
    personaId?: string;
    positioningId?: string;
  };
  created: string;
}

export interface ResearchChat {
  id: string;
  user: string;
  project: string;
  persona: string;
  title?: string;
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }>;
  summary?: string;
  created: string;
  updated: string;
}

export interface MentorChat {
  id: string;
  user: string;
  title: string;
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }>;
  message_count: number;
  created: string;
  updated: string;
}

export interface Payment {
  id: string;
  user: string;
  amount: number;
  currency: string;
  status: 'pending' | 'success' | 'failed' | 'refunded';
  robokassa_inv_id?: string;
  subscription_type: 'creator' | 'top_creator';
  subscription_months: number;
  paid_at?: string;
  created: string;
}

export interface PromoCode {
  id: string;
  code: string;
  type: 'trial' | 'discount' | 'full';
  discount_percent?: number;
  trial_days?: number;
  max_uses?: number;
  current_uses: number;
  valid_until?: string;
  is_active: boolean;
}

export interface Feedback {
  id: string;
  user: string;
  rating: number;
  question_answer?: string;
  comment?: string;
  source?: 'sidebar' | 'proactive';
  user_tier?: string;
  page?: string;
  status?: 'new' | 'reviewed' | 'archived';
  created: string;
  updated: string;
}

// Хелперы

export const isAuthenticated = (): boolean => pb.authStore.isValid;

export const getCurrentUser = (): User | null => pb.authStore.record as User | null;

export const getToken = (): string => pb.authStore.token;

export const logout = (): void => {
  pb.authStore.clear();
};

/**
 * Type guard для проверки что объект соответствует интерфейсу User
 */
export function isUser(obj: unknown): obj is User {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'id' in obj &&
    typeof (obj as Record<string, unknown>).id === 'string' &&
    'email' in obj &&
    typeof (obj as Record<string, unknown>).email === 'string'
  );
}
